// ЭКРАН ШАГА 5 — красная линия в кадре покоя. B7-3 `3e619875cbb88161b355c870f62512cf`.
//
// ИСТОЧНИК — борд `Life Map - s5 Red Line v7.html` (sha256 `6a1fbb7a…`), `view()` `:616–639`, чарт
// `chartHTML()` `:490–574`, бровь `carrierEyebrow()` `:579`, доказательства `evidenceHTML()` `:588`,
// карточка имени `nameHTML()` `:610`; вместе с реестром отставаний шага 5 в `WIZARD-CANON.md`.
//
// ⭐ ДАННЫЕ — ВЬЮ §IV, А НЕ СВОЙ РАСЧЁТ. `viewIV` (`src/render/viewFromPassport.js`) уже сводит выход
// движка в то, что печатает красная линия паспорта: раскладку несущей, класс геометрии, уровень,
// членов (у раскола — в порядке брови), бледные источники и НЕКАПНУТЫЙ список носителей
// `carrierStages.indices` (`s486.d1`). Шаг 5 — второй такт той же находки, и второй расчёт был бы
// вторым голосом о той же линии. Строки — общий узел (`findingBank.js`, `chartCopy.js`), геометрия —
// `s5Chart.js` (B7-2), табы — `s5Tabs.js`.
//
// ⭐ REVEAL (B7-4, `s952.d2`): гашение семи линий, вайп красной, каденция точек, `skip`, «один раз за
// сессию» — расписание и память в `s5Reveal.js`. Экран без пропа `reveal` — кадр покоя, ровно как
// сдан в B7-3: разметка покоя от reveal не зависит, и синтетика по раскладкам её не меняла.
//
// ⭐ ШТОРКА СРЕЗА ЭТАПА (B7-5, `s956.d1`): адрес `#s5/N` (этап с единицы), а не `#s5/cut/i` борда —
// грамматика `address.js` разбирает его без правок. Открывают колонка-носитель, таб и карточка
// доказательства; «назад» браузера закрывает (шторка живёт адресом, а не состоянием экрана).
// Разбор адреса — `cutWhere`, одно место. Состояния «ничего не отвечено» нет — оно недостижимо
// (`s454.d1` + гейт шага 3 `s495.d1`).
//
// ⛔ SYSTEM 2 ДЕГРАДИРУЕТ ТИХО (`s453.d2`, `s454`): нет цитаты — на её месте `t.ev.silent`, и больше
// ничего. Ни «загрузки», ни «недоступно» экран не печатает.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { HANDLED, useWizard } from '../WizardProvider.jsx';
import { useDocumentPassport } from '../documentPassport.js';
import { formatAddress, parseAddress } from '../address.js';
import { viewIV } from '../../render/viewFromPassport.js';
import { ROMAN } from '../../roman.js';
import { findingParts, onePoint } from '../../copy/findingBank.js';
import { EV_SILENT_OVERLAY, carrierEyebrow, italicPhraseOverlay } from '../../copy/chartCopy.js';
import { CH, chartGeometry, filledCarrier, paleSources, redLineMask, smoothD, spansD, yV } from './s5Chart.js';
import { S4_LAYERS, filledSeries, isLive } from './s4Chart.js';
import { placeTabsS5 } from './s5Tabs.js';
import { hotPole } from './deckSlider.js';
import {
  CUT_CLOSE, CUT_NO_ANSWER, EV_CARD_CAP, EV_TAP_HINT, GTAG_ARC, GTAG_HEAD, NAME_RAIL, S5_BACK,
  S5_COUNT_TAIL, S5_CTA, SCATTER_FLOOR, SKIP_LABEL, cutHead, evidenceHead, s5Count, whoLine,
} from './s5Copy.js';
import {
  EV_STAGGER_S, PT_TRANSITION_REVEAL, PT_TRANSITION_SKIP, RV, WIPE_TRANSITION,
  dotFrames, prefersReducedMotion, rememberReveal, revealPlayed, revealSchedule,
} from './s5Reveal.js';

/** Печатное имя слоя шага 5 — имя деки (`sense of security`), как на шаге 4. */
const LAYER = Object.fromEntries(S4_LAYERS.map((L) => [L.key, L]));
const lname = (k) => LAYER[k].name;

const yy = (mmYYYY) => (mmYYYY ? `’${mmYYYY.slice(-2)}` : '');

// ---- вью-модель экрана (чистая, без DOM) -------------------------------------------------------

/**
 * Всё, что печатает экран шага 5, из паспорта. Отдельной функцией ради синтетики по шести
 * раскладкам: тест подаёт паспорт с нужным `carrierConfig`, не собирая ввод под движок.
 *
 * @param {object} passport выход `buildPassport`
 */
export function stepFiveView(passport) {
  const v = viewIV(passport);
  const n = v.carrier.length;
  const parts = findingParts(v);
  const eyb = carrierEyebrow(v, lname);
  const ev = v.carrierIdx.filter((i) => Number.isInteger(i) && i >= 0 && i < n);
  const cards = ev.length > EV_CARD_CAP ? ev.slice(-EV_CARD_CAP) : ev; // шесть самых свежих
  const quoteAt = new Map((v.evidence || []).filter((e) => e.quote).map((e) => [e.stage, e.quote]));
  // ⭐ При scatter «кто» — АКТИВНЫЕ слои (`s954.d2`): тронутые хоть на одном этапе, порядок `LAYERS`.
  // Читается прямо из движка (`system1.meta.activeLayers`, `insights/index.js`): `viewIV` их не
  // несёт, а второй расчёт «тронутости» здесь был бы вторым голосом. Нет поля — запасной случай.
  const active = (passport.system1.meta && passport.system1.meta.activeLayers) || [];
  const names = v.carrierConfig === 'scatter'
    ? S4_LAYERS.filter((L) => active.includes(L.key)).map((L) => L.name)
    : v.members.map(lname);
  const stages = v.stages || Array.from({ length: n }, () => ({ name: '', start: null, end: null }));
  const range = (s) => `${yy(s.start)} — ${s.current ? 'present' : yy(s.end)}`;
  return {
    n,
    config: v.carrierConfig,
    members: v.members.slice(),
    // Для шторки среза: имя и годы этапа (v7 `st.nm`, `st.dt` — без римской, она в шапке) и цитата.
    stageCut: stages.map((s, i) => ({ name: s.name, range: range(s), quote: quoteAt.get(i) ?? null })),
    title: parts,
    italic: italicPhraseOverlay(v), // фолбэк `the red line.` — общий с §IV
    eyebrow: eyb,
    carrier: v.carrier,
    sources: paleSources(v.carrierSourceLines),
    sliders: passport.input.wizard.sliders.map((s) => (s && s.values ? s.values : s)),
    ev,
    // Этапы, чью шторку можно открыть, — ровно этапы с карточкой доказательства (`s455.d1`): при
    // кэпе шесть (`s691.d2`) старшие носители карточки не получают и шторку не открывают.
    cuts: cards.slice(),
    who: whoLine(v.carrierConfig, names),
    floor: v.carrierConfig === 'scatter' ? SCATTER_FLOOR : null,
    evidence: ev.length === 0 ? null : {
      head: evidenceHead(ev.length),
      // Нумерация сквозная по носителям (`evNumOffset()` `:420`): карточка печатает свой номер,
      // а не позицию в капнутом массиве — таб 03 на чарте и карточка 03 в списке один этап.
      cards: cards.map((i, j) => ({
        i,
        no: String(j + 1 + (ev.length - cards.length)).padStart(2, '0'),
        name: stages[i].name,
        dt: `${ROMAN[i]} · ${range(stages[i])}`,
        quote: quoteAt.get(i) ?? null,
      })),
    },
    years: stages.map((s) => ({ label: s.current ? 'now' : yy(s.start), now: !!s.current })),
  };
}

/** Голый адрес шага 5 — цель поправки недопустимого адреса шторки и закрытия без своей записи. */
export const S5_ADDRESS = formatAddress({ step: 5 });

/** Адрес шторки среза этапа `i` (индекс с нуля) — `#s5/{i+1}` (`s956.d1` п. 1). */
export const cutAddress = (i) => formatAddress({ step: 5, card: i + 1 });

/**
 * Какая шторка открыта по разобранному адресу (`s956.d1`, по образцу `deckWhere`).
 *
 * `{ open: i }` — индекс этапа с нуля (только этап с карточкой доказательства, `s455.d1`); `{ open: null, redirect }` — адрес шторки недопустим, и экран
 * подменяет запись на `#s5` (п. 4). Голый `#s5` — `{ open: null, redirect: null }`.
 *
 * Недопустимо: `scatter` (`s460.d1` — срез не открывается вовсе); номер вне этапов; подэкран
 * (`#s5/2/b` — у шторки буквы нет, п. 1); ⚠️ этап, который линию НЕ несёт. Последнее — сужение
 * «вне диапазона» решения, и оно названо: шапка `t.cut.hd` утверждает `carries the line`, а цели
 * тапа (колонка, таб, карточка) есть только у носителей, так что такой адрес достижим лишь руками.
 *
 * @param {{step: number, card: number|null, sub: string|null}|null} parsed
 * @param {{config: string, ev: number[]}} view вью-модель экрана
 */
export function cutWhere(parsed, view) {
  if (!parsed || parsed.step !== 5 || !Number.isInteger(parsed.card)) return { open: null, redirect: null };
  const i = parsed.card - 1;
  const ok = view.config !== 'scatter' && parsed.sub === null && view.cuts.includes(i);
  return ok ? { open: i, redirect: null } : { open: null, redirect: S5_ADDRESS };
}

/**
 * Подпись полюса — те же три подписи деки и тот же порог `hotPole`, что горели под дорожкой на
 * шаге 3. ⚠️ Не порог v7 (`v>=1` → правый, `:700`): на значении ровно `1` борд сказал бы `full of
 * life`, а человек, ставивший ползунок, видел `steady`. Расхождение — строка реестра шага 5.
 */
function poleWord(L, v) {
  const hot = hotPole(v);
  if (hot === 'l') return L.poles.left;
  if (hot === 'r') return L.poles.right;
  return L.poles.mid;
}

/**
 * Всё, что печатает шторка среза этапа `i` (v7 `cutSheet()` `:701–714`). Чистая, ради синтетики.
 *
 * Семь рядов — `sliders[i]` в порядке слоёв контракта; несущие (`members`) — курсивом (`.car`).
 * Слой без ответа на этапе — пустая дорожка и подпись `no answer` (байт шага 4, см. `CUT_NO_ANSWER`);
 * пунктира `.nul` нет — снят на шторке среза шага 4 (`s616.d3`), шторка шага 5 — та же сущность (`s957.d1`).
 * ⛔ Подписи-источника `vertical cut · … · catalog s175–s179` (v7 `:710`) нет — §19 спеки, как у
 * листов шага 3.
 */
export function cutView(view, i) {
  const st = view.stageCut[i];
  const row = view.sliders[i] || {};
  const mem = new Set(view.members);
  return {
    i,
    head: cutHead(i, view.n, (k) => ROMAN[k]),
    name: st.name,
    dt: st.range,
    quote: st.quote,
    rows: S4_LAYERS.map((L) => {
      const raw = row[L.key];
      const v = Number.isFinite(raw) && hotPole(raw) ? raw : null;
      return {
        key: L.key, name: L.name, color: L.color, car: mem.has(L.key), v,
        w: v === null ? 0 : (Math.abs(v) / 3) * 50,
        pole: v === null ? CUT_NO_ANSWER : poleWord(L, v),
      };
    }),
  };
}


// ---- экран -----------------------------------------------------------------------------------

/** Кадр покоя в шкале reveal: дальше любого кадра. */
export const REST = Infinity;

/**
 * Часы reveal: последний сыгранный кадр, мс, или `REST`.
 *
 * ⭐ РЕШЕНИЕ «ИГРАТЬ ИЛИ НЕТ» ПРИНИМАЕТСЯ ОДИН РАЗ — НА МОНТИРОВАНИИ (инициализатор `useState`). Токен
 * `'s5'` пишется по ходу (конец, `skip`, reduced motion), и читать его на каждом рендере значило бы
 * погасить reveal его же записью. Повторный вход — уже новое монтирование.
 *
 * ⚠️ СТРАХОВОЧНЫЙ 3300 — отдельный таймер, а не последний кадр расписания (v7 `:682`): если кадры
 * не дошли (вкладка в фоне, троттлинг таймеров), экран всё равно встаёт в покой. `schedule` —
 * параметром ради этого теста: расписание, у которого кадры потерялись.
 */
export function useRevealClock(m, played, schedule = revealSchedule) {
  const [start] = useState(() => (played || prefersReducedMotion() ? REST : 0));
  const [t, setT] = useState(start);
  const [skipped, setSkipped] = useState(false);
  const mRef = useRef(m);
  const scheduleRef = useRef(schedule);

  useEffect(() => {
    if (start === REST) return undefined;
    const tick = (ms) => () => setT((cur) => (cur === REST || ms >= RV.END ? REST : Math.max(cur, ms)));
    const ids = scheduleRef.current(mRef.current).map((ms) => setTimeout(tick(ms), ms));
    ids.push(setTimeout(() => setT(REST), RV.SAFETY));
    return () => ids.forEach(clearTimeout);
  }, [start]);

  const skip = useCallback(() => {
    setSkipped(true);
    setT(REST);
  }, []);
  return { t, skipped, skip };
}

export default function StepFive() {
  const { session, publishGate, update, view: address, goTo, router } = useWizard();
  const view = stepFiveView(useDocumentPassport(session));
  const where = cutWhere(parseAddress(address), view);
  // ⭐ ВХОД ПРЯМО НА `#s5/N` (ссылка, перезагрузка) — кадр покоя с первого рендера, затем шторка
  // (`s956.d1` п. 3). Часы решают «играть или нет» один раз на монтировании, и открытая шторка
  // на этом монтировании значит «не играть»; токен `'s5'` пишет тот же эффект входа в покой, что и
  // `skip`. Тап посреди reveal — другой путь, `openCut` ниже.
  const { t, skipped, skip } = useRevealClock(
    view.ev.length, revealPlayed(session.revealsPlayed) || where.open !== null,
  );
  const live = t !== REST;
  // Играл ли reveal на этом монтировании (решение принимают часы один раз — первый кадр не REST).
  const startedLive = useRef(t !== REST).current;

  // Недопустимый адрес шторки — ПОДМЕНА записи на `#s5` (`s956.d1` п. 4), как `deckRedirect`
  // шага 3: «назад» не должен вернуть на адрес, который экран только что поправил.
  useEffect(() => {
    if (where.redirect) goTo(where.redirect, { replace: true });
  }, [where.redirect, goTo]);

  // Тап по носителю. Посреди reveal — сперва `settle()` борда (= `skip`: кадр покоя, ход `.3s`,
  // токен), потом шторка (`s956.d1` п. 3, DoR фазы 2 п. 1). Новая запись истории: «назад» её снимет.
  // ⛔ Шторка уже открыта — второй записи поверх нет (находка qa-reviewer, cc-180): иначе `close`
  // снял бы только верхнюю, и шторка осталась бы открытой. `inert` экрана ниже убирает цели тапа из
  // обхода, но программный клик не глушит (замер Н37) — защита в коде нужна обе.
  const openCut = useCallback((i) => {
    if (where.open !== null) return;
    if (live) skip();
    goTo(cutAddress(i));
  }, [where.open, live, skip, goTo]);

  // Закрытие кнопкой, вуалью, Escape — «назад», если запись своя (v7 `closeSheet()` `:715`), иначе
  // подмена на `#s5` (шторку открыли ссылкой или перезагрузкой — позади чужая страница).
  const closeCut = useCallback(() => {
    if (!router.back()) goTo(S5_ADDRESS, { replace: true });
  }, [router, goTo]);
  const open = where.open;

  const remember = useCallback(() => update((s) => {
    const played = rememberReveal(s.revealsPlayed);
    return played === s.revealsPlayed ? s : { ...s, revealsPlayed: played };
  }), [update]);

  // ⭐ ТОКЕН ПИШЕТСЯ НА ВХОДЕ В ПОКОЙ — по концу, по `skip`, по страховке и сразу при reduced motion
  // (`s952.d2` пп. 2, 4, 5). На уже сыгранном входе запись — no-op (прежний массив по ссылке).
  useEffect(() => {
    if (!live) remember();
  }, [live, remember]);

  // CTA и возврат печатает оболочка; экран объявляет надписи и вид кнопки каналом гейта. Таблица
  // `footerNav.js` не трогается: она отвечает на «какая пара внизу», а не на «какими словами».
  // ⭐ `tone: 'solid'` — amber solid ТОЛЬКО на шаге 5 (`s456.d3`); v7 рисует outline — отставание.
  // ⭐ `muted` — CTA видима с первого кадра и приглушена до конца reveal (`s952.d2` п. 6, образец
  // `s493.d3`). Приглушена, а не закрыта: нажатие ведёт дальше, как в покое (`s493.d3` отменил
  // «CTA через 3 секунды» — анти-скипа нет). Уход со шага посреди reveal пишет токен тем же
  // нажатием: человек видел экран, и возврат с шага 6 — сразу покой (п. 3).
  // ⭐ ШТОРКА ОТКРЫТА — ПОДВАЛ МОЛЧИТ (как лист `?` шага 3): у борда вуаль накрывает и ряд кнопок.
  // Наша вуаль тоже его накрывает (`position:fixed`), но клавиатура до подвала дотянется — и
  // `HANDLED` без перехода значит «нажатие принято, идти некуда».
  // ⭐ LAYOUT-ЭФФЕКТ (B6-7, `s977.d2` п. 4): вид кнопки — до первой отрисовки, иначе на входе виден
  // контур оболочки и ход в сплошную (мигание на повторном входе, замер CC 08.10).
  // `fade` — выход из приглушения плавный только на естественной посадке: reveal играл, `skip`
  // (и тап по носителю, он зовёт тот же `skip`) не нажимали.
  useLayoutEffect(() => {
    publishGate({
      label: S5_CTA, backLabel: S5_BACK,
      tone: 'solid',
      muted: live,
      fade: !live && startedLive && !skipped,
      ...(live ? { onPress: remember, onBack: remember } : {}),
      ...(open !== null ? { onPress: () => HANDLED, onBack: () => false } : {}),
    });
  });
  useEffect(() => () => publishGate(null), [publishGate]);

  return (
    <>
      <StepFiveScreen view={view} reveal={live || skipped ? { t, skipped, onSkip: skip } : undefined} onCut={openCut}
        inert={open !== null} />
      <CutSheet view={view} open={open} onClose={closeCut} />
    </>
  );
}

/** Ход шторки — `transform .3s` (v7 `.sheet` `:148`); столько же живёт узел после закрытия. */
export const CUT_MS = 300;

/**
 * Шторка среза этапа (v7 `cutSheet()` `:701–714`, вид `.veil`/`.sheet`/`.cut`/`.crow` `:146–172`).
 *
 * Открыта она или нет, решает АДРЕС (`open`), а не своё состояние: «назад» браузера закрывает её
 * тем же путём, что и кнопка. Своё состояние у неё одно — ДОЖИТЬ `CUT_MS` после закрытия, чтобы
 * уехать вниз, а не исчезнуть (у борда узел `#sheet` постоянный, у нас — монтируется по адресу).
 */
export function CutSheet({ view, open, onClose }) {
  // Последний открытый этап и «узел ушёл» — поправляются при рендере (образец React «состояние из
  // пропа»), а не эффектом: синхронный `setState` в эффекте — лишний проход отрисовки (линт).
  const [last, setLast] = useState(open);
  const [gone, setGone] = useState(open === null);
  const [entered, setEntered] = useState(false);
  if (open !== null && open !== last) setLast(open);
  if (open !== null && gone) setGone(false);
  if (open === null && entered) setEntered(false); // повторное открытие въедет снова
  const shown = gone ? null : (open ?? last);
  const on = entered && open !== null;
  const closeRef = useRef(null);

  // `.on` — кадром позже монтирования, как `requestAnimationFrame` у листа `?` шага 3: иначе
  // переход с `translateY(102%)` не сыграет. Закрытие — узел доживает `CUT_MS` и уходит.
  useEffect(() => {
    if (open !== null) {
      const id = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(id);
    }
    const id = setTimeout(() => setGone(true), CUT_MS);
    return () => clearTimeout(id);
  }, [open]);

  // Фокус на `close`, как у листа `?` шага 3; `preventScroll` — без него полоса шага едет к кнопке.
  useEffect(() => {
    if (open !== null && closeRef.current) closeRef.current.focus({ preventScroll: true });
  }, [open, shown]);

  useEffect(() => {
    if (open === null) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (shown === null || !view.stageCut[shown]) return null;
  const cut = cutView(view, shown);
  return (
    <>
      <div data-testid="s5-veil" className={`s5-veil${on ? ' on' : ''}`} onClick={onClose} />
      <div data-testid="s5-sheet" data-stage={cut.i} className={`s5-sheet${on ? ' on' : ''}`} role="dialog" aria-modal="true">
        <span className="cor tl" /><span className="cor tr" />
        <div data-testid="s5-cut-hd" className="sc">{cut.head}</div>
        <h4>{cut.name}</h4>
        <div className="dt">{cut.dt}</div>
        <div data-testid="s5-cut-q" className={`q${cut.quote ? '' : ' silent'}`}>{cut.quote ?? EV_SILENT_OVERLAY}</div>
        <div className="cut">
          {cut.rows.map((r) => (
            <div key={r.key} data-testid={`s5-crow-${r.key}`} className={`crow${r.car ? ' car' : ''}${r.v === null ? ' nil' : ''}`}>
              <span className="nm">{r.name}</span>
              <span className="bar">
                <span className="base" /><span className="mid" />
                {r.v === null ? null : (
                  <span className="fl" style={r.v >= 0
                    ? { background: r.color, left: '50%', width: `${r.w}%` }
                    : { background: r.color, right: '50%', width: `${r.w}%` }} />
                )}
              </span>
              <span className="pw">{r.pole}</span>
            </div>
          ))}
        </div>
        <button ref={closeRef} type="button" data-testid="s5-cut-close" onClick={onClose}>{CUT_CLOSE}</button>
      </div>
    </>
  );
}

/** Разметка экрана по вью-модели. Экспортирована ради синтетики по раскладкам. */
export function StepFiveScreen({ view, reveal, onCut, inert = false }) {
  const { title, eyebrow } = view;
  const t = reveal ? reveal.t : REST;
  const live = t !== REST;
  const copyOn = t >= RV.COPY;
  const evOn = t >= RV.EV;
  return (
    // `inert` на время шторки (`aria-modal` её не заменяет: без него Tab уводит с `close` на карточку
    // другого носителя). Атрибут строкой, как у шага 2 (Н37, `s942.d2`): React 18 булева не знает.
    <div data-testid="s5-screen" className="s5-screen" data-config={view.config}
      {...(live ? { 'data-phase': 'reveal' } : {})} inert={inert ? '' : undefined}>
      <p data-testid="s5-count" className="s5-count">{s5Count(view.n)}<b>{S5_COUNT_TAIL}</b></p>
      <h2 data-testid="s5-title" className="s5-title">
        {title.predicate === null
          ? <em>{`${title.subject}.`}</em>
          : <><em>{title.subject}</em>{title.separator}<span className="rd">{title.predicate}</span></>}
      </h2>

      <div data-testid="s5-gtag" className="s5-gtag">
        <b>{GTAG_HEAD}</b><span className="sep">·</span><span>{GTAG_ARC}</span>
        {view.ev.length ? (
          <><span className="sep">·</span><span data-testid="s5-frac">{view.ev.length}<span className="sep">/</span>{view.n}</span></>
        ) : null}
        <span className="rt">
          {eyebrow.label}<span className="sep"> · </span><b data-testid="s5-eyebrow">{eyebrow.body}</b>
        </span>
      </div>

      <Chart view={view} reveal={reveal} onCut={onCut} />
      {/* `skip` — только в фазе анимации (v7 `:634`, `s952.d2` п. 4), ведёт в покой и пишет токен.
          Обёртка нулевой высоты: кнопка ложится поверх слота строки чтения (он пуст до 2600) и
          раскладку не двигает. Место — не байт борда, причина в `wizard-s5.css`. */}
      {live ? (
        <div className="s5-skipwrap">
          <button type="button" data-testid="s5-skip" className="s5-skip" onClick={reveal.onSkip}>{SKIP_LABEL}</button>
        </div>
      ) : null}

      <div data-testid="s5-italic" className={`s5-iq${copyOn ? ' on' : ''}`}>{view.italic}</div>

      <div className="s5-below">
        <div data-testid="s5-name" className={`s5-nameb${copyOn ? ' on' : ''}`}>
          <div className="bd">
            <div className="rail-lb">{NAME_RAIL}</div>
            <div data-testid="s5-who" className="who">{view.who}</div>
          </div>
        </div>
        {view.floor === null ? null : (
          // Строка floor приходит вместе с карточкой имени: у `Scatter Final` reveal нет вовсе, и
          // строка, стоящая под ещё невидимой карточкой, была бы единственным текстом фона.
          // Ход — тот же `.55s`, что у `.s5-nameb`. Не байт борда — названо в сдаче B7-4.
          <div data-testid="s5-floor" className="s5-floorb"
            {...(reveal ? { style: { opacity: copyOn ? 1 : 0, transition: 'opacity .55s' } } : {})}>
            <div className="fl">{view.floor}</div>
          </div>
        )}
        {view.evidence === null ? null : (
          <>
            <div className="s5-evhd">
              <span data-testid="s5-evhd">{view.evidence.head}</span><span className="ln" />
              <span className="tap">{EV_TAP_HINT}</span>
            </div>
            <div data-testid="s5-evlist" className="s5-evlist">
              {view.evidence.cards.map((c, j) => (
                // Карточка — цель тапа в срез этапа (B7-5, v7 `bind()` `:721`).
                // Каскад во время reveal — `transition-delay:${j*.14}s` (v7 `:603`); после `skip` — 0
                // (`settle()` `:697`).
                <button key={c.i} type="button" data-testid={`s5-ev-${c.i}`} className={`s5-ev${evOn ? ' on' : ''}`}
                  onClick={onCut ? () => onCut(c.i) : undefined}
                  {...(reveal ? { style: { transitionDelay: live ? `${(j * EV_STAGGER_S).toFixed(2)}s` : '0s' } } : {})}>
                  <span className="no">{c.no}</span>
                  <span className="bd">
                    <span className="nm">{c.name}</span>
                    <span className="dt">{c.dt}</span>
                    <span className={`q${c.quote ? '' : ' silent'}`}>{c.quote ?? EV_SILENT_OVERLAY}</span>
                  </span>
                  <span className="arw">⤢</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Chart({ view, reveal, onCut }) {
  const geom = chartGeometry(view.n);
  const { W, xMid } = geom;
  const mask = redLineMask(view.carrier, geom);
  const c = filledCarrier(view.carrier);
  const evS = new Set(view.ev);
  // Цель тапа в срез — только у этапа с карточкой (`s455.d1`); старший носитель за кэпом карточек
  // точку и таб сохраняет, но ничего не открывает.
  const cutS = new Set(view.cuts);
  const tabs = placeTabsS5(view.ev, xMid, (i) => yV(c[i]), mask.red.d, { top: CH.top, h: CH.h, W });
  const step = view.n <= 5 ? 1 : (view.n <= 9 ? 2 : 3);
  const f = (x) => x.toFixed(1);

  // ---- reveal (B7-4). Без `reveal` — кадр покоя, и ни одного атрибута ниже не появляется. ----
  const t = reveal ? reveal.t : REST;
  const live = t !== REST;
  // Ход непрозрачности: во время reveal `.5s ease-out` (v7 `:647`), после `skip` — `.3s` (`:685`).
  const pt = reveal ? { style: { transition: live ? PT_TRANSITION_REVEAL : PT_TRANSITION_SKIP } } : {};
  const op = (v) => (live ? { opacity: v } : {});
  const dotAt = live ? dotFrames(view.ev.length) : [];
  const shown = (j) => !live || t >= dotAt[j];
  const srcKeys = new Set(view.sources.map((s) => s.key));
  // Пять не-несущих (`.rest`, v7 `:500`): есть только в reveal — в покое они на нуле (`settle()`
  // `:687`), и узел покоя от reveal не меняется. После `skip` доживают до конца своего хода `.3s`.
  // ⛔ Слой, не тронутый ни на одном этапе, линии не имеет вовсе (`s601.d1`, как на шаге 4): у
  // борда серия мока есть всегда, у живого ввода — нет, и `filledSeries` на всё-`null` падает.
  const rawOf = (key) => view.sliders.map((row) => {
    const x = row ? row[key] : null;
    return Number.isFinite(x) ? x : null;
  });
  const valsOf = (key) => filledSeries(rawOf(key));
  const rest = reveal ? S4_LAYERS.filter((L) => !srcKeys.has(L.key) && isLive(rawOf(L.key))) : [];
  const wiped = t >= RV.WIPE;
  return (
    <div className="s5-cwrap">
      <span className="cor tl" /><span className="cor tr" /><span className="cor bl" /><span className="cor br" />
      <div className={`s5-cscroll${geom.panX ? '' : ' nofit'}`}>
        <svg data-testid="s5-chart" viewBox={`0 0 ${W} ${CH.h}`} width={W} height={CH.h}>
          <defs>
            <linearGradient id="s5-rl" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={W} y2="0">
              <stop offset="0" stopColor="#7A1E10" stopOpacity=".55" />
              <stop offset=".35" stopColor="#C8482B" />
              <stop offset=".75" stopColor="#E8623F" />
              <stop offset="1" stopColor="#C8482B" stopOpacity=".8" />
            </linearGradient>
            <filter id="s5-rg" x="-10%" y="-40%" width="120%" height="180%"><feGaussianBlur stdDeviation={mask.glow.blur} /></filter>
            {/* Маска штриха (v7 `:563`, ход `:653–657`): `scaleX(0)` до кадра 1120, затем 1 с до `1`. */}
            {live ? (
              <clipPath id="s5-wipe" clipPathUnits="userSpaceOnUse">
                <rect data-testid="s5-wiper" x="0" y="0" width={W} height={CH.h}
                  style={{ transform: wiped ? 'scaleX(1)' : 'scaleX(0)', transition: wiped ? WIPE_TRANSITION : 'none' }} />
              </clipPath>
            ) : null}
          </defs>
          <line x1="0" y1={yV(0)} x2={W} y2={yV(0)} stroke="rgba(143,136,122,.16)" strokeDasharray="2 6" strokeWidth=".6" />
          <line x1={geom.axisX} y1={CH.top - 4} x2={geom.axisX} y2={CH.bot + 6} stroke="#3E3323" strokeWidth=".7" strokeDasharray="2 4" />
          <line x1="0" y1={CH.bot + 8} x2={W} y2={CH.bot + 8} stroke="#2E2619" strokeWidth=".5" strokeDasharray="2 4" />
          {rest.length ? (
            <g>
              {rest.map((L) => (
                <path key={L.key} data-testid={`s5-rest-${L.key}`} d={smoothD(valsOf(L.key), geom)} fill="none" stroke={L.color}
                  strokeWidth="1.4" strokeDasharray={L.dash ?? undefined} opacity={live && t < RV.FADE ? '.85' : '0'}
                  strokeLinecap="round" {...pt} />
              ))}
            </g>
          ) : null}
          {/* бледные источники в покое — `.38` (`settle()` `:687`); в кадре 0 — `.85` (v7 `:650`) */}
          <g>
            {view.sources.map((s) => {
              const li = S4_LAYERS.findIndex((L) => L.key === s.key);
              const d = spansD(valsOf(s.key), geom);
              return (
                <path key={s.key} data-testid={`s5-src-${s.key}`} d={d} fill="none" stroke={S4_LAYERS[li].color}
                  strokeWidth={s.strokeWidth} strokeDasharray={s.dash ?? undefined} opacity={live && t < RV.FADE ? '.85' : '.38'}
                  strokeLinecap="round" {...pt} />
              );
            })}
          </g>
          <g {...(live ? { clipPath: 'url(#s5-wipe)' } : {})}>
            <path data-testid="s5-glow" className={`s5-glow${t >= RV.PULSE ? ' pulse' : ''}`} d={mask.glow.d} fill="none" stroke="#C8482B" strokeWidth={mask.glow.strokeWidth}
              strokeLinecap="round" strokeLinejoin="round" opacity={live && !wiped ? '0' : '.22'} filter="url(#s5-rg)" />
            <path data-testid="s5-red" d={mask.red.d} fill="none" stroke="url(#s5-rl)" strokeWidth={mask.red.strokeWidth}
              strokeLinecap="round" strokeLinejoin="round" {...op(wiped ? 1 : 0)} />
          </g>
          <g>
            {/* Н41: при одной точке остальные этапы не отвечены — отметок на дорисованной высоте нет */}
            {(onePoint(view) ? [] : c).map((vv, i) => (evS.has(i) ? null : (
              <g key={`m${i}`} data-testid={`s5-mut-${i}`} {...op(t >= RV.DOTS ? 1 : 0)} {...pt}>
                <circle cx={f(xMid(i))} cy={f(yV(vv))} r="4.6" fill="#0E0B08" />
                <circle cx={f(xMid(i))} cy={f(yV(vv))} r="2.9" fill="#5A554A" opacity=".65" />
              </g>
            )))}
          </g>
          {/* колонки-носители — цель тапа в срез (B7-5, v7 `bind()` `:720`; табы — тоже `.hit`) */}
          <g>
            {view.ev.map((i) => (
              <rect key={`h${i}`} data-testid={`s5-hit-${i}`} className={cutS.has(i) ? 'hit' : undefined} x={f(xMid(i) - geom.colW / 2)} y={CH.top - 14}
                width={f(geom.colW)} height={CH.bot - CH.top + 30} fill="transparent"
                onClick={onCut && cutS.has(i) ? () => onCut(i) : undefined} />
            ))}
          </g>
          <g>
            {view.ev.map((i, j) => (
              <g key={`d${i}`} data-testid={`s5-dot-${i}`} className={`s5-dot${shown(j) ? ' pulse' : ''}`}
                {...op(shown(j) ? 1 : 0)} {...pt}>
                <circle className="halo" cx={f(xMid(i))} cy={f(yV(c[i]))} r="12" fill="rgba(200,72,43,.18)" />
                <circle cx={f(xMid(i))} cy={f(yV(c[i]))} r="5.6" fill="#C8482B" stroke="#0E0B08" strokeWidth="1.6" />
              </g>
            ))}
            {tabs.map((tb, j) => (
              <g key={`t${tb.i}`} data-testid={`s5-tab-${tb.i}`} className={`s5-tab${cutS.has(tb.i) ? ' hit' : ''}`} data-tab-n={tb.n} data-tab-tier={tb.tier}
                data-tab-side={tb.side} {...(tb.fallback ? { 'data-tab-fallback': tb.fallback } : {})}
                transform={`translate(${f(tb.cx)},${f(tb.y + tb.dy)})`} {...op(shown(j) ? 1 : 0)} {...pt}
                onClick={onCut && cutS.has(tb.i) ? () => onCut(tb.i) : undefined}>
                <line x1="0" y1={tb.up ? 7 : -7} x2={f(tb.x - tb.cx)} y2={f((tb.up ? -6 : 6) - tb.dy)} stroke="rgba(200,72,43,.7)" strokeWidth=".7" strokeDasharray="1.5 2.5" />
                <rect x="-11" y="-7" width="22" height="14" fill="#C8482B" />
                <text x="0" y="4" textAnchor="middle">{tb.n}</text>
              </g>
            ))}
          </g>
          <g>
            {Array.from({ length: view.n }, (_, i) => (
              <text key={`r${i}`} className={`s5-stg${evS.has(i) ? ' on' : ''}`} x={f(xMid(i))} y={CH.top - 9} textAnchor="middle">{ROMAN[i]}</text>
            ))}
          </g>
          <g>
            {view.years.map((y, i) => ((i % step === 0 || y.now) ? (
              <text key={`y${i}`} className={`s5-yr${y.now ? ' now' : ''}`} x={f(xMid(i))} y={CH.bot + 22} textAnchor="middle">{y.label}</text>
            ) : null))}
          </g>
        </svg>
      </div>
    </div>
  );
}
